import { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export function Lightbox({ images, index, onClose, onNavigate }) {
  const open = index !== null && index >= 0;

  const prev = useCallback(() => onNavigate((index - 1 + images.length) % images.length), [index, images.length, onNavigate]);
  const next = useCallback(() => onNavigate((index + 1) % images.length), [index, images.length, onNavigate]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, prev, next]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[80] flex items-center justify-center bg-[#1F2421]/90 backdrop-blur-sm p-4 sm:p-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          role="dialog"
          aria-modal="true"
        >
          <button
            data-testid="lightbox-close"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-5 right-5 text-[#F9F8F3]/80 hover:text-[#F9F8F3] transition-colors z-10"
          >
            <X size={28} />
          </button>
          <button
            data-testid="lightbox-prev"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Previous image"
            className="absolute left-3 sm:left-6 text-[#F9F8F3]/70 hover:text-[#F9F8F3] transition-colors z-10"
          >
            <ChevronLeft size={36} />
          </button>
          <motion.img
            key={images[index].src}
            src={images[index].src}
            alt={images[index].alt}
            className="max-h-[85vh] max-w-full object-contain shadow-2xl"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.35 }}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            data-testid="lightbox-next"
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Next image"
            className="absolute right-3 sm:right-6 text-[#F9F8F3]/70 hover:text-[#F9F8F3] transition-colors z-10"
          >
            <ChevronRight size={36} />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
